
import { defineComponent, ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { analyticsOutline } from 'ionicons/icons';
import type {
  SkillsApiResponse,
  ChartDataPoint,
  LegendItem,
  TooltipData,
  LocDataPoint,
  CodeMetrics
} from '../types/SkillsApi';
import { AXIS_DEFAULTS, isPartialMonth, makeTimeScale, parseMonth, stretchNote, timeTicks } from '../lib/timeAxis.mjs';
import { OTHER, OTHER_COLOR, SELECTION_DEFAULTS, languageColor, planChart } from '../lib/languagePalette.mjs';

export default defineComponent({
  name: 'LocalSkillsChart',
  props: {
    username: {
      type: String,
      required: true
    },
    legend: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: Boolean,
      default: false
    },
    // Comma list of languages to show (in stack order), or 'auto' for the
    // shared selection policy (src/lib/languagePalette.mjs, same as the
    // profile SVG). '' shows every language.
    skills: {
      type: String,
      default: ''
    },
    // Selection policy knobs for skills="auto" (defaults shared with the profile generator)
    peakThreshold: {
      type: Number,
      default: SELECTION_DEFAULTS.peakThreshold
    },
    otherTarget: {
      type: Number,
      default: SELECTION_DEFAULTS.otherTarget
    },
    languageCap: {
      type: Number,
      default: SELECTION_DEFAULTS.cap
    },
    showOtherSkills: {
      type: Boolean,
      default: false
    },
    branding: {
      type: Boolean,
      default: true
    },
    apiUrl: {
      type: String,
      default: 'http://localhost:3000'
    },
    showLinesOfCode: {
      type: Boolean,
      default: false
    },
    locDisplayMode: {
      type: String,
      default: 'cumulative',
      validator: (value: string) => ['cumulative', 'period', 'net'].includes(value)
    },
    locSmoothing: {
      type: Boolean,
      default: true
    },
    // Time axis (defaults shared with tools/skills-chart-generator.mjs)
    aiEraStart: {
      type: String,
      default: AXIS_DEFAULTS.aiEraStart
    },
    recentMonths: {
      type: Number,
      default: AXIS_DEFAULTS.recentMonths
    },
    recentShare: {
      type: Number,
      default: AXIS_DEFAULTS.recentShare
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const apiData = ref<SkillsApiResponse | null>(null);
    const chartData = ref<ChartDataPoint[]>([]);
    const locData = ref<LocDataPoint[]>([]);
    const legendItems = ref<LegendItem[]>([]);
    const tooltipData = ref<TooltipData | null>(null);
    const chartSvg = ref<SVGElement | null>(null);
    const otherBreakdown = ref<any>(null);
    const legendTooltip = ref<{ skill: string; x: number; y: number } | null>(null);
    
    // Hover state for legend highlighting
    const hoveredSkill = ref<string | null>(null);
    const isHovering = ref(false);
    
    // Add reactive key to force re-renders when data changes
    const dataRevision = ref(0);

    // Chart dimensions - fully responsive with conditional margins for LoC axis
    const svgWidth = ref(0);
    const svgHeight = ref(0);
    const baseMargin = { top: 10, right: 30, bottom: 38, left: 50 };
    const margin = computed(() => ({
      ...baseMargin,
      right: props.showLinesOfCode ? 70 : baseMargin.right // Extra space for LoC axis
    }));
    const containerRef = ref<HTMLElement | null>(null);

    // Computed properties
    const showLegend = computed(() => props.legend);
    const showTooltip = computed(() => props.tooltip);
    const showBranding = computed(() => props.branding);

    const autoSelect = computed(() => props.skills.trim() === 'auto');

    // Shared language plan (selection, stack order, colours) for skills="auto".
    // Uses the default groups (src/lib/languagePalette.mjs DEFAULT_GROUPS):
    // grouped layers are drawn from the summed member series (see processApiData).
    const plan = computed(() => {
      if (!autoSelect.value || !apiData.value) return null;
      return planChart(apiData.value.scores, {
        peakThreshold: props.peakThreshold,
        otherTarget: props.otherTarget,
        cap: props.languageCap
      });
    });

    const specifiedSkills = computed((): string[] => {
      if (autoSelect.value) return plan.value ? plan.value.order.filter(l => l !== OTHER) : [];
      return props.skills
        ? props.skills.split(',').map(skill => skill.trim())
        : [];
    });

    // Group name -> its member languages that occur in the data (legend + tooltip).
    const groupMembersInData = computed((): Record<string, string[]> => {
      const out: Record<string, string[]> = {};
      if (!plan.value || !apiData.value) return out;
      // lower-case -> data spelling (data keys may differ in case from linguist names)
      const present = new Map<string, string>();
      apiData.value.scores.forEach(s => s.languages.forEach(l => present.set(l.language.toLowerCase(), l.language)));
      for (const g of plan.value.groups) {
        if (!plan.value.shown.includes(g.name)) continue;
        out[g.name] = g.members.map(m => present.get(m.toLowerCase())).filter((m): m is string => !!m);
      }
      return out;
    });

    // Compute which languages are included in "Other"
    const otherSkillsList = computed(() => {
      if (!apiData.value || !props.showOtherSkills || specifiedSkills.value.length === 0) {
        return [];
      }
      
      const allLanguages = new Set<string>();
      (plan.value ? plan.value.scores : apiData.value.scores).forEach(score => {
        score.languages.forEach(lang => {
          allLanguages.add(lang.language);
        });
      });
      
      // Filter out the specified skills to get the "Other" skills
      return Array.from(allLanguages)
        .filter(lang => !specifiedSkills.value.includes(lang))
        .sort();
    });

    const visibleSkills = computed(() => {
      return legendItems.value
        .filter(item => item.visible)
        .map(item => item.skill);
    });

    // Piecewise time scale shared with the profile SVG generator (src/lib/timeAxis.mjs).
    const timeScale = computed(() => {
      const left = margin.value.left;
      const right = Math.max(left, svgWidth.value - margin.value.right);
      if (!chartData.value.length || svgWidth.value <= 0) {
        return makeTimeScale({ min: 0, max: 0, left, right, recentMonths: 0 });
      }
      let min = Infinity;
      let max = -Infinity;
      for (const p of chartData.value) {
        const t = p.date.getTime();
        if (t < min) min = t;
        if (t > max) max = t;
      }
      return makeTimeScale({ min, max, left, right, recentMonths: props.recentMonths, recentShare: props.recentShare });
    });

    // The series ends at the refresh date, so its last month is month-to-date.
    const partialEnd = computed(() => {
      const n = chartData.value.length;
      if (!n) return false;
      const asOf = apiData.value ? (apiData.value as { asOf?: string }).asOf : undefined;
      return isPartialMonth(chartData.value[n - 1].date, asOf);
    });

    const xTicks = computed(() => timeTicks(timeScale.value, { partialEnd: partialEnd.value }));

    const recentNote = computed(() => {
      const s = timeScale.value;
      return s.breakX === null ? '' : stretchNote(s, svgWidth.value - margin.value.right - s.breakX - 4);
    });

    const axisBreakPath = computed(() => {
      const bx = timeScale.value.breakX;
      const y = svgHeight.value - margin.value.bottom;
      return bx === null ? '' : `M ${bx - 5} ${y + 4} L ${bx - 1} ${y - 4} M ${bx + 1} ${y + 4} L ${bx + 5} ${y - 4}`;
    });

    const aiEraX = computed((): number | null => {
      if (!props.aiEraStart) return null;
      const s = timeScale.value;
      const t = parseMonth(props.aiEraStart).getTime();
      return t > s.min && t < s.max ? s.x(t) : null;
    });

    // Colours: github-linguist colours + documented overrides, shared with
    // the profile SVG (src/lib/languagePalette.mjs). Fixed per language, so
    // stack order and toggling never change a colour.
    // Computed map of skill opacities for efficient lookup
    const skillOpacityMap = computed(() => {
      const opacities = new Map<string, number>();

      legendItems.value.forEach(legendItem => {
        const skill = legendItem.skill;

        // If skill is not visible (disabled), always show at low opacity
        if (!legendItem.visible) {
          opacities.set(skill, 0.1);
          return;
        }

        // If no skill is being hovered, use normal visibility
        if (!isHovering.value) {
          opacities.set(skill, 0.9);
          return;
        }

        // If this skill is being hovered, show at full opacity
        if (hoveredSkill.value === skill) {
          opacities.set(skill, 0.9);
        } else {
          // If another skill is being hovered, dim this skill (grey out effect)
          opacities.set(skill, 0.2);
        }
      });

      return opacities;
    });

    // Calculate skill opacity based on visibility and hover state
    const getSkillOpacity = (skill: string): number => {
      const opacity = skillOpacityMap.value.get(skill);
      return opacity !== undefined ? opacity : 0.9;
    };

    // "Dockerfile 3.2 · Makefile 1.1 · …" for a group layer at one data point.
    const groupBreakdown = (skill: string, point: ChartDataPoint | undefined): string | null => {
      const members = groupMembersInData.value[skill];
      if (!members || !point) return null;
      const parts = members
        .map(m => ({ m, v: Math.round(((point[m] as number) || 0) * 10) / 10 }))
        .filter(x => x.v > 0)
        .sort((a, b) => b.v - a.v)
        .map(x => `${x.m} ${x.v}`);
      return parts.length ? parts.join(' · ') : null;
    };

    const getSkillColor = (skill: string): string => {
      if (skill === OTHER) return OTHER_COLOR;
      const c = plan.value ? plan.value.colors[skill] : undefined;
      return c || languageColor(skill);
    };

    const aggregateDataByMonth = (dataPoints: ChartDataPoint[]): ChartDataPoint[] => {
      if (dataPoints.length === 0) return dataPoints;
      
      // Group data points by month-year
      const monthlyData = new Map<string, ChartDataPoint>();
      
      dataPoints.forEach(point => {
        const monthKey = `${point.date.getFullYear()}-${String(point.date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData.has(monthKey)) {
          // Create new monthly aggregate using the first day of the month
          const aggregateDate = new Date(point.date.getFullYear(), point.date.getMonth(), 1);
          monthlyData.set(monthKey, { date: aggregateDate });
        }
        
        const monthlyPoint = monthlyData.get(monthKey);
        if (!monthlyPoint) return;
        
        // Aggregate skill scores using weighted average (favoring recent activity)
        Object.keys(point).forEach(key => {
          if (key !== 'date' && typeof point[key] === 'number') {
            const currentValue = (monthlyPoint[key] as number) || 0;
            const newValue = point[key] as number;
            
            // Use weighted average that slightly favors newer data
            // This creates smoother transitions than max() while still responding to activity
            if (currentValue === 0) {
              monthlyPoint[key] = newValue;
            } else {
              // Weight new data 60%, existing 40% for smooth blending
              monthlyPoint[key] = (newValue * 0.6) + (currentValue * 0.4);
            }
          }
        });
      });
      
      // Convert back to array and sort by date
      const aggregatedPoints = Array.from(monthlyData.values());
      aggregatedPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      return aggregatedPoints;
    };

    const applyMovingAverageSmoothing = (dataPoints: ChartDataPoint[], windowSize = 3): ChartDataPoint[] => {
      if (dataPoints.length <= windowSize) return dataPoints;

      const smoothedPoints: ChartDataPoint[] = [];

      dataPoints.forEach((point, index) => {
        const smoothedPoint: ChartDataPoint = { date: point.date };

        // Calculate the window boundaries
        const halfWindow = Math.floor(windowSize / 2);
        const startIndex = Math.max(0, index - halfWindow);
        const endIndex = Math.min(dataPoints.length - 1, index + halfWindow);

        // For each skill/language, calculate moving average
        Object.keys(point).forEach(key => {
          if (key !== 'date' && typeof point[key] === 'number') {
            let sum = 0;
            let count = 0;

            // Collect values in the window
            for (let i = startIndex; i <= endIndex; i++) {
              const value = dataPoints[i][key] as number;
              if (value !== undefined && value > 0) {
                sum += value;
                count++;
              }
            }

            // Apply moving average if we have data
            if (count > 0) {
              smoothedPoint[key] = sum / count;
            } else {
              smoothedPoint[key] = point[key]; // Fallback to original value
            }
          }
        });

        smoothedPoints.push(smoothedPoint);
      });

      return smoothedPoints;
    };

    const addLinearInterpolation = (dataPoints: ChartDataPoint[]): ChartDataPoint[] => {
      if (dataPoints.length < 2) return dataPoints;

      const interpolatedPoints: ChartDataPoint[] = [];
      
      // Get all skill names that appear in the data
      const allSkills = new Set<string>();
      dataPoints.forEach(point => {
        Object.keys(point).forEach(key => {
          if (key !== 'date' && typeof point[key] === 'number') {
            allSkills.add(key);
          }
        });
      });

      // Sort data points by date
      const sortedPoints = [...dataPoints].sort((a, b) => a.date.getTime() - b.date.getTime());

      for (let i = 0; i < sortedPoints.length; i++) {
        const currentPoint = sortedPoints[i];
        interpolatedPoints.push({ ...currentPoint });

        // Add interpolated points between current and next point if gap is large
        if (i < sortedPoints.length - 1) {
          const nextPoint = sortedPoints[i + 1];
          const timeDiff = nextPoint.date.getTime() - currentPoint.date.getTime();
          const monthsGap = timeDiff / (30 * 24 * 60 * 60 * 1000); // Approximate months

          // If gap is larger than 2 months, add interpolated points
          if (monthsGap > 2) {
            const interpolationSteps = Math.min(3, Math.floor(monthsGap / 2)); // Max 3 interpolated points
            
            for (let step = 1; step <= interpolationSteps; step++) {
              const ratio = step / (interpolationSteps + 1);
              const interpolatedDate = new Date(
                currentPoint.date.getTime() + ratio * timeDiff
              );

              const interpolatedPoint: ChartDataPoint = { date: interpolatedDate };

              // Interpolate each skill value
              allSkills.forEach(skill => {
                const currentValue = (currentPoint[skill] as number) || 0;
                const nextValue = (nextPoint[skill] as number) || 0;
                
                // Use exponential decay interpolation for more realistic skill progression
                // Skills tend to decay over time, so use weighted interpolation
                if (currentValue > 0 || nextValue > 0) {
                  const decayFactor = 0.1; // Small decay factor for interpolation
                  const baseInterpolation = currentValue + ratio * (nextValue - currentValue);
                  const decayAdjustment = baseInterpolation * (1 - decayFactor * ratio);
                  
                  interpolatedPoint[skill] = Math.max(0, decayAdjustment);
                }
              });

              interpolatedPoints.push(interpolatedPoint);
            }
          }
        }
      }

      return interpolatedPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
    };


    const applyLocSmoothing = (points: LocDataPoint[]): LocDataPoint[] => {
      const windowSize = Math.min(3, Math.floor(points.length / 3));
      if (windowSize < 2) return points;
      
      return points.map((point, index) => {
        const startIndex = Math.max(0, index - Math.floor(windowSize / 2));
        const endIndex = Math.min(points.length - 1, index + Math.floor(windowSize / 2));
        
        let sum = 0;
        let count = 0;
        
        for (let i = startIndex; i <= endIndex; i++) {
          sum += points[i].cumulativeLines || 0;
          count++;
        }
        
        return {
          ...point,
          cumulativeLines: count > 0 ? Math.round(sum / count) : point.cumulativeLines
        };
      });
    };

    const processLocData = (codeMetrics: CodeMetrics[]) => {
      const locPoints: LocDataPoint[] = [];
      let cumulativeLines = 0;
      
      // Sort by date to ensure proper cumulative calculation
      const sortedMetrics = [...codeMetrics].sort((a, b) => a.date.localeCompare(b.date));
      
      for (const metric of sortedMetrics) {
        let displayValue: number;
        
        // Calculate cumulative if mode is cumulative
        if (props.locDisplayMode === 'cumulative') {
          cumulativeLines += metric.netLines;
          displayValue = cumulativeLines;
        } else if (props.locDisplayMode === 'net') {
          displayValue = metric.netLines;
        } else { // period
          displayValue = metric.linesAdded;
        }
        
        locPoints.push({
          date: new Date(metric.date),
          linesAdded: metric.linesAdded,
          linesDeleted: metric.linesDeleted,
          linesChanged: metric.linesChanged,
          netLines: metric.netLines,
          commitCount: metric.commitCount,
          filesTouched: metric.filesTouched,
          cumulativeLines: displayValue
        });
      }
      
      // Apply smoothing if enabled
      if (props.locSmoothing && locPoints.length > 2) {
        locData.value = applyLocSmoothing(locPoints);
      } else {
        locData.value = locPoints;
      }
    };

    // Raw scores -> monthly, gap-interpolated, 3-month-smoothed chart points.
    const seriesFromScores = (scores: SkillsApiResponse['scores'], allSkills?: Set<string>): ChartDataPoint[] => {
      let dataPoints: ChartDataPoint[] = [];
      scores.forEach(score => {
        const point: ChartDataPoint = { date: new Date(score.date) };
        score.languages.forEach(lang => {
          point[lang.language] = lang.score;
          if (allSkills) allSkills.add(lang.language);
        });
        dataPoints.push(point);
      });
      dataPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
      // Aggregate by month, fill gaps between sparse points, then 3-month moving average
      dataPoints = aggregateDataByMonth(dataPoints);
      dataPoints = addLinearInterpolation(dataPoints);
      dataPoints = applyMovingAverageSmoothing(dataPoints, 3);
      dataPoints.sort((a, b) => a.date.getTime() - b.date.getTime());
      return dataPoints;
    };

    // Per-member series for group layers (tooltip breakdown only), keyed by time.
    let memberSeries = new Map<number, ChartDataPoint>();

    const processApiData = (data: SkillsApiResponse) => {
      const allSkills = new Set<string>();
      // Grouped layers (plan.groups, e.g. "Build & Infrastructure") are drawn
      // from plan.scores, where members are already summed before smoothing —
      // exactly what the profile SVG generator draws. The ungrouped series is
      // smoothed separately for the per-member tooltip breakdown.
      const drawn = plan.value ? plan.value.scores : data.scores;
      const grouped = drawn !== data.scores;
      const dataPoints = seriesFromScores(drawn, allSkills);
      memberSeries = new Map();
      if (grouped) {
        for (const p of seriesFromScores(data.scores)) memberSeries.set(p.date.getTime(), p);
      }
      chartData.value = dataPoints;

      // Create legend items
      const skills = Array.from(allSkills);
      const filteredSkills = specifiedSkills.value.length > 0
        ? specifiedSkills.value.filter(skill => skills.includes(skill))
        : skills;

      legendItems.value = filteredSkills.map(skill => ({
        skill,
        color: getSkillColor(skill),
        visible: true
      }));

      // Handle "Other" skills if enabled
      if (props.showOtherSkills && specifiedSkills.value.length > 0) {
        const otherSkills = skills.filter(skill => !specifiedSkills.value.includes(skill));
        if (otherSkills.length > 0) {
          // Calculate "Other" values for each data point
          chartData.value.forEach(point => {
            let otherTotal = 0;
            otherSkills.forEach(skill => {
              const skillValue = (point[skill] as number) || 0;
              otherTotal += skillValue;
            });
            // Add the aggregated "Other" value to the data point
            point['Other'] = otherTotal;
          });
          
          legendItems.value.push({
            skill: OTHER,
            color: OTHER_COLOR,
            visible: true
          });
        }
      }
    };

    const fetchOtherBreakdown = async () => {
      try {
        const isStaticFile = props.apiUrl.endsWith('.json');
        let url: string;
        
        if (isStaticFile) {
          // Static file - use companion other-breakdown.json file
          url = '/data/other-breakdown.json';
        } else {
          // Live API endpoint - use monitor URL
          const monitorUrl = props.apiUrl.replace(':3000', ':4000');
          url = `${monitorUrl}/api/languages/other-breakdown`;
        }
        
        const response = await fetch(url);
        if (response.ok) {
          otherBreakdown.value = await response.json();
        }
      } catch (error) {
        console.warn('Could not fetch Other breakdown:', error);
      }
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        // Check if apiUrl is a static JSON file or live API endpoint
        const isStaticFile = props.apiUrl.endsWith('.json');
        let url: string;
        
        if (isStaticFile) {
          // Static JSON file - use direct URL
          url = props.apiUrl;
        } else {
          // Live API endpoint - add cache-busting parameter and LoC parameter
          const cacheBuster = Date.now();
          const locParam = props.showLinesOfCode ? '&includeLoC=true' : '';
          url = `${props.apiUrl}/api/v2/users/${props.username}/tech_score_history?_t=${cacheBuster}${locParam}`;
        }
        
        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            // Handle no data case gracefully - this is expected for users without data
            error.value = `No skills data available for ${props.username} yet`;
            return;
          }
          throw new Error(`API request failed: ${response.status}`);
        }

        const data: SkillsApiResponse = await response.json();
        apiData.value = data;
        
        // Fetch "Other" breakdown if enabled
        if (props.showOtherSkills) {
          await fetchOtherBreakdown();
        }
        
        processApiData(data);
        
        // Process LoC data if available
        if (data.codeMetrics && props.showLinesOfCode) {
          processLocData(data.codeMetrics);
        }
        
        // Force Vue to complete all reactive updates
        await nextTick();
        
        // Increment data revision to force immediate reactivity update
        dataRevision.value++;
        
        // Force another tick to ensure all computed properties update
        await nextTick();
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load skills data';
        console.error('Skills API Error:', err);
      } finally {
        loading.value = false;
      }
    };

    const getXPosition = (date: Date): number => timeScale.value.x(date);

    // Calculate stacked data for stack plot
    const stackedData = computed(() => {
      const stacked: Map<string, ChartDataPoint> = new Map();
      
      chartData.value.forEach((point) => {
        const stackedPoint: ChartDataPoint = { date: point.date };
        let cumulativeValue = 0;
        
        // Stack visible skills only
        visibleSkills.value.forEach(skill => {
          const value = (point[skill] as number) || 0;
          stackedPoint[`${skill}_base`] = cumulativeValue;
          stackedPoint[skill] = value;
          cumulativeValue += value;
        });
        
        stackedPoint['_total'] = cumulativeValue;
        stacked.set(point.date.toISOString(), stackedPoint);
      });
      
      const result = Array.from(stacked.values());
      
      return result;
    });

    // Pre-compute requested skills list to avoid repeated parsing
    const requestedSkillsList = computed(() => {
      const allAvailableSkills = legendItems.value.map(item => item.skill);
      // skills="auto": every shown layer ("Other" is appended separately by the tooltip)
      if (autoSelect.value) return allAvailableSkills.filter(skill => skill !== OTHER);
      return props.skills
        ? props.skills.split(',').map((s: string) => s.trim()).filter(skill => allAvailableSkills.includes(skill))
        : allAvailableSkills;
    });

    // Create index map for LoC data for faster lookups
    const locDataMap = computed(() => {
      const map = new Map<string, LocDataPoint>();
      locData.value.forEach(point => {
        const dateKey = point.date.toISOString().split('T')[0];
        map.set(dateKey, point);
      });
      return map;
    });

    // Memoized tooltip data cache to avoid recalculation
    const tooltipCache = ref<Map<number, any>>(new Map());

    // Mouse move throttling with RAF
    let rafId: number | null = null;
    let lastDataIndex = -1;

    const updateTooltip = (event: MouseEvent, rect: DOMRect, x: number) => {
      // Find nearest data point (by date: the x axis is piecewise, not linear in index)
      if (x < margin.value.left || x > svgWidth.value - margin.value.right || !stackedData.value.length) {
        tooltipData.value = null;
        lastDataIndex = -1;
        return;
      }

      const t = timeScale.value.invert(x);
      const pts = stackedData.value;
      let lo = 0;
      let hi = pts.length - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (pts[mid].date.getTime() <= t) lo = mid;
        else hi = mid;
      }
      const dataIndex = Math.abs(pts[hi].date.getTime() - t) < Math.abs(t - pts[lo].date.getTime()) ? hi : lo;

      // If hovering over the same data point, just update position
      if (dataIndex === lastDataIndex && tooltipData.value) {
        tooltipData.value = {
          ...tooltipData.value,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10
        };
        return;
      }

      lastDataIndex = dataIndex;

      // Check cache first
      if (tooltipCache.value.has(dataIndex)) {
        const cachedData = tooltipCache.value.get(dataIndex);
        tooltipData.value = {
          ...cachedData,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10
        };
        return;
      }

      const dataPoint = stackedData.value[dataIndex];

      if (dataPoint) {
        // Build tooltip skills array efficiently
        const rawPoint = memberSeries.get(dataPoint.date.getTime());
        const tooltipSkills = requestedSkillsList.value.map(skill => ({
          name: skill,
          score: Math.round(((dataPoint[skill] as number) || 0) * 10) / 10,
          color: getSkillColor(skill),
          breakdown: groupBreakdown(skill, rawPoint)
        }));

        // Add "Other" as a single aggregated entry if it exists
        if (dataPoint['Other'] && (dataPoint['Other'] as number) > 0) {
          tooltipSkills.push({
            name: 'Other',
            score: Math.round(((dataPoint['Other'] as number) || 0) * 10) / 10,
            color: OTHER_COLOR,
            breakdown: null
          });
        }

        const skills = tooltipSkills
          .filter(skill => skill.score > 0)
          .sort((a, b) => b.score - a.score);

        // Find corresponding LoC data point using index map
        let locDataForTooltip = null;
        if (props.showLinesOfCode && locDataMap.value.size > 0) {
          const dateKey = dataPoint.date.toISOString().split('T')[0];
          const locPoint = locDataMap.value.get(dateKey);

          if (locPoint) {
            locDataForTooltip = {
              value: locPoint.cumulativeLines || 0,
              linesAdded: locPoint.linesAdded,
              linesDeleted: locPoint.linesDeleted,
              netLines: locPoint.netLines,
              commitCount: locPoint.commitCount
            };
          }
        }

        const tooltipInfo = {
          date: dataPoint.date.toLocaleDateString(),
          skills,
          locData: locDataForTooltip
        };

        // Cache the computed tooltip data
        tooltipCache.value.set(dataIndex, tooltipInfo);

        // Limit cache size to prevent memory bloat
        if (tooltipCache.value.size > 100) {
          const firstKey = tooltipCache.value.keys().next().value;
          tooltipCache.value.delete(firstKey);
        }

        tooltipData.value = {
          ...tooltipInfo,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10
        };
      }
    };

    // Calculate maximum Y value for auto-scaling
    const maxYValue = computed(() => {
      if (!stackedData.value.length) {
        // Use a sensible default that doesn't depend on svgHeight
        return 1000;
      }
      
      // Get all _total values and sort them
      const totals = stackedData.value.map(d => (d['_total'] as number) || 0).sort((a, b) => a - b);
      
      // 95th percentile (+20% headroom) to tame outliers, but never below the
      // actual peak (+5%): the newest months must not be clipped flat.
      const percentile95Index = Math.floor(totals.length * 0.95);
      const p95 = totals[percentile95Index] || totals[totals.length - 1];
      const maxTotal = Math.max(p95, (totals[totals.length - 1] * 1.05) / 1.2);
      
      // Ensure we have a positive value
      if (maxTotal <= 0) return 1000;
      
      // Add padding and round to nice values (dynamic based on data scale)
      const baseUnit = Math.pow(10, Math.floor(Math.log10(maxTotal)) - 1);
      const result = Math.ceil(maxTotal * 1.2 / baseUnit) * baseUnit;
      
      // Ensure we return a positive finite number
      return isFinite(result) && result > 0 ? result : 1000;
    });

    const yAxisLabels = computed(() => {
      if (!maxYValue.value) return [];
      const max = maxYValue.value;
      const numLabels = 5;
      const step = max / (numLabels - 1);
      return Array.from({ length: numLabels }, (_, i) => Math.round(i * step));
    });

    // LoC-specific computed properties
    const maxLocValue = computed(() => {
      if (!props.showLinesOfCode || !locData.value.length) return 0;
      
      const locValues = locData.value.map(d => d.cumulativeLines || 0);
      const max = Math.max(...locValues);
      
      if (max <= 0) return 1000;
      
      // Add padding and round to nice values
      const baseUnit = Math.pow(10, Math.floor(Math.log10(max)) - 1);
      const result = Math.ceil(max * 1.2 / baseUnit) * baseUnit;
      
      return isFinite(result) && result > 0 ? result : 1000;
    });

    const locAxisLabels = computed(() => {
      if (!props.showLinesOfCode || !maxLocValue.value) return [];
      const max = maxLocValue.value;
      const numLabels = 5;
      const step = max / (numLabels - 1);
      return Array.from({ length: numLabels }, (_, i) => Math.round(i * step));
    });

    const getLocYPosition = (value: number): number => {
      if (svgHeight.value <= 0 || maxLocValue.value <= 0) {
        return svgHeight.value - margin.value.bottom;
      }
      
      const ratio = Math.max(0, Math.min(1, value / maxLocValue.value));
      const chartHeight = svgHeight.value - margin.value.top - margin.value.bottom;
      
      if (chartHeight <= 0) return svgHeight.value - margin.value.bottom;
      
      return svgHeight.value - margin.value.bottom - ratio * chartHeight;
    };

    const getYPosition = (value: number): number => {
      if (svgHeight.value <= 0 || maxYValue.value <= 0) {
        return svgHeight.value - margin.value.bottom;
      }
      
      const ratio = Math.max(0, Math.min(1, value / maxYValue.value)); // Clamp ratio between 0 and 1
      const chartHeight = svgHeight.value - margin.value.top - margin.value.bottom;
      
      // Ensure we don't get invalid positions
      if (chartHeight <= 0) return svgHeight.value - margin.value.bottom;
      
      return svgHeight.value - margin.value.bottom - ratio * chartHeight;
    };

    // Separate path/color data (expensive, recalculates only on data/dimension changes)
    // from opacity (cheap, recalculates on hover)
    const skillPathData = computed(() => {
      if (!stackedData.value.length || svgWidth.value <= 0 || svgHeight.value <= 0) {
        return [];
      }

      return visibleSkills.value.map(skill => {
        const points: string[] = [];
        let pathString = '';

        try {
          // Start from bottom left (at the base of this skill's stack)
          const firstPoint = stackedData.value[0];
          const baseValue = (firstPoint[`${skill}_base`] as number) || 0;
          const startX = getXPosition(firstPoint.date);
          const startY = getYPosition(baseValue);

          // Validate starting position
          if (isFinite(startX) && isFinite(startY)) {
            points.push(`M ${startX} ${startY}`);

            // Draw top line (stacked values)
            stackedData.value.forEach(point => {
              const base = (point[`${skill}_base`] as number) || 0;
              const value = (point[skill] as number) || 0;
              const x = getXPosition(point.date);
              const y = getYPosition(base + value);

              // Validate coordinates before adding
              if (isFinite(x) && isFinite(y)) {
                points.push(`L ${x} ${y}`);
              }
            });

            // Draw bottom line (base of stack) in reverse
            for (let i = stackedData.value.length - 1; i >= 0; i--) {
              const point = stackedData.value[i];
              const base = (point[`${skill}_base`] as number) || 0;
              const x = getXPosition(point.date);
              const y = getYPosition(base);

              // Validate coordinates before adding
              if (isFinite(x) && isFinite(y)) {
                points.push(`L ${x} ${y}`);
              }
            }

            points.push('Z');
            pathString = points.join(' ');
          }
        } catch (error) {
          console.warn('Error generating area path for skill:', skill, error);
        }

        return {
          skill,
          path: pathString,
          color: getSkillColor(skill)
        };
      });
    });

    // Combine path data with opacity (recalculates on hover, but cheaply)
    const skillPaths = computed(() => {
      return skillPathData.value.map(pathData => {
        const opacity = skillOpacityMap.value.get(pathData.skill);
        return {
          ...pathData,
          opacity: opacity !== undefined ? opacity : 0.9
        };
      });
    });

    const getAreaPath = (skill: string): string => {
      const skillPath = skillPaths.value.find(sp => sp.skill === skill);
      return skillPath ? skillPath.path : '';
    };

    const toggleSkill = (skill: string) => {
      const item = legendItems.value.find(item => item.skill === skill);
      if (item) {
        item.visible = !item.visible;
      }
    };

    const formatLocLabel = (value: number): string => {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
      }
      return value.toString();
    };

    const getLocTrendPath = (): string => {
      if (!locData.value.length || svgWidth.value <= 0 || svgHeight.value <= 0) {
        return '';
      }

      const points: string[] = [];
      
      try {
        // Start the path
        const firstPoint = locData.value[0];
        const startX = getXPosition(firstPoint.date);
        const startY = getLocYPosition(firstPoint.cumulativeLines || 0);
        
        if (!isFinite(startX) || !isFinite(startY)) {
          return '';
        }
        
        points.push(`M ${startX} ${startY}`);
        
        // Draw line to each subsequent point
        locData.value.slice(1).forEach(point => {
          const x = getXPosition(point.date);
          const y = getLocYPosition(point.cumulativeLines || 0);
          
          if (isFinite(x) && isFinite(y)) {
            points.push(`L ${x} ${y}`);
          }
        });
        
        return points.join(' ');
      } catch (error) {
        console.warn('Error generating LoC trend path:', error);
        return '';
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!showTooltip.value || !chartSvg.value) return;

      const rect = chartSvg.value.getBoundingClientRect();
      const x = event.clientX - rect.left;

      // Use requestAnimationFrame for smooth, efficient updates
      if (rafId !== null) {
        return; // Already scheduled, skip this event
      }

      rafId = window.requestAnimationFrame(() => {
        updateTooltip(event, rect, x);
        rafId = null;
      });
    };

    const hideTooltip = () => {
      tooltipData.value = null;
      lastDataIndex = -1;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
    
    const handleLegendHover = (event: MouseEvent, skill: string) => {
      // Set hover state for highlighting
      hoveredSkill.value = skill;
      isHovering.value = true;
      
      // Show tooltip for "Other" and for group layers (member list)
      if (skill === 'Other' || groupMembersInData.value[skill]) {
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        const containerRect = containerRef.value ? containerRef.value.getBoundingClientRect() : null;
        
        if (containerRect) {
          legendTooltip.value = {
            skill,
            x: rect.left - containerRect.left + rect.width / 2,
            y: rect.top - containerRect.top + rect.height + 20 // Position well below the legend item
          };
        }
      } else {
        legendTooltip.value = null;
      }
    };
    
    const hideLegendTooltip = () => {
      // Clear hover state for highlighting
      hoveredSkill.value = null;
      isHovering.value = false;
      legendTooltip.value = null;
    };

    const updateDimensions = () => {
      if (!containerRef.value) {
        return false;
      }
      
      const containerWidth = containerRef.value.clientWidth;
      const containerHeight = containerWidth * 0.6; // Increased height ratio for better utilization
      
      // If container has no width, it's not properly rendered yet
      if (containerWidth === 0) {
        return false;
      }
      
      // Enforce minimum dimensions to prevent rendering issues
      const minWidth = 300;
      const minHeight = 200;
      
      svgWidth.value = Math.max(containerWidth, minWidth);
      svgHeight.value = Math.max(containerHeight, minHeight);
      
      return svgWidth.value > 0 && svgHeight.value > 0;
    };


    // Lifecycle
    onMounted(async () => {
      // Wait for DOM to be ready and container to be rendered
      await nextTick();
      
      // Give the browser a chance to render the container
      await new Promise(resolve => setTimeout(resolve, 0));
      
      // Try to get actual container dimensions
      if (!updateDimensions()) {
        // If container is still not available, wait a bit more
        await new Promise(resolve => setTimeout(resolve, 50));
        updateDimensions();
      }
      
      // Load data after we have dimensions
      await fetchData();
      
      // Force one more dimension update after data loads in case container sized up
      updateDimensions();
      
      // Add resize listener with proper handling
      const handleResize = () => {
        if (updateDimensions() && chartData.value.length > 0) {
          // Trigger reactivity update if we have valid dimensions and data
          dataRevision.value++;
        }
      };
      window.addEventListener('resize', handleResize);
      
      // Store cleanup function for resize listener
      onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
        // Cleanup tooltip RAF
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      });
    });

    watch(() => props.username, async () => {
      // Reset data revision when username changes to ensure fresh render
      dataRevision.value = 0;
      await fetchData();
    });
    
    // Watch for API URL changes to refresh data
    watch(() => props.apiUrl, async () => {
      await fetchData();
    });

    // Watch dimension changes and trigger reactivity updates
    watch([svgWidth, svgHeight], async ([newWidth, newHeight], [oldWidth, oldHeight]) => {
      // Only trigger if dimensions actually changed and we have valid data
      if ((newWidth !== oldWidth || newHeight !== oldHeight) && 
          newWidth > 0 && newHeight > 0 && 
          chartData.value.length > 0) {
        
        // Increment dataRevision to force SVG re-render with new dimensions
        // This is safer than temporarily clearing chartData
        dataRevision.value++;
        
        // Use nextTick to ensure the reactive update completes
        await nextTick();
      }
    });
    
    // Watch for when all chart data dependencies are ready and force render
    watch([chartData, legendItems, svgWidth, svgHeight], async () => {
      // If we have data, legend items, and valid dimensions, force a render
      if (chartData.value.length > 0 && 
          legendItems.value.length > 0 && 
          svgWidth.value > 0 && 
          svgHeight.value > 0) {
        
        // Small delay to ensure all reactive dependencies are settled
        await nextTick();
        
        // Force a re-render by incrementing dataRevision
        dataRevision.value++;
      }
    });

    return {
      analyticsOutline,
      containerRef,
      loading,
      error,
      chartSvg,
      svgWidth,
      svgHeight,
      margin,
      dataRevision,
      showLegend,
      showTooltip,
      showBranding,
      visibleSkills,
      otherSkillsList,
      groupMembersInData,
      legendItems,
      tooltipData,
      legendTooltip,
      timeScale,
      xTicks,
      recentNote,
      axisBreakPath,
      aiEraX,
      yAxisLabels,
      locAxisLabels,
      stackedData,
      maxYValue,
      skillPaths,
      getSkillColor,
      getSkillOpacity,
      getAreaPath,
      getXPosition,
      getYPosition,
      getLocYPosition,
      toggleSkill,
      formatLocLabel,
      getLocTrendPath,
      handleMouseMove,
      hideTooltip,
      handleLegendHover,
      hideLegendTooltip,
      locData
    };
  }
});
